import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import ToothModal from "./ToothModal";
import OdontogramAIReviewCard from "./ai/OdontogramAIReviewCard";
import { listOdontogramReviews } from "../services/contextualAiApi";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8001"
    : "");

const COLORS = {
  carious: "#EF4444",
  restored: "#10B981",
  extracted: "#6B7280",
  fractured: "#F59E0B",
  crown: "#60A5FA",
  implant: "#0EA5E9",
  root_canal: "#EC4899",
  prosthesis: "#8B5CF6",
};

const STATUS_LABELS = {
  healthy: "Saudável",
  carious: "Cariado",
  restored: "Restaurado",
  extracted: "Extraído",
  fractured: "Fraturado",
  crown: "Coroa",
  implant: "Implante",
  root_canal: "Tratamento de canal",
  prosthesis: "Prótese",
};

const DENTITION_OPTIONS = [
  { value: "permanent", label: "Permanente" },
  { value: "deciduous", label: "Decídua" },
  { value: "mixed", label: "Mista" },
];

const PERMANENT_GROUPS = [
  { label: "Molares", span: 3 },
  { label: "Pré-molares", span: 2 },
  { label: "Canino", span: 1 },
  { label: "Incisivos", span: 4 },
  { label: "Canino", span: 1 },
  { label: "Pré-molares", span: 2 },
  { label: "Molares", span: 3 },
];

const DECIDUOUS_GROUPS = [
  { label: "Molares", span: 2 },
  { label: "Canino", span: 1 },
  { label: "Incisivos", span: 4 },
  { label: "Canino", span: 1 },
  { label: "Molares", span: 2 },
];

const ARCHES = [
  {
    key: "deciduous-upper",
    type: "deciduous",
    label: "Dentição decídua superior",
    teeth: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
    groups: DECIDUOUS_GROUPS,
    upper: true,
  },
  {
    key: "permanent-upper",
    type: "permanent",
    label: "Arcada permanente superior",
    teeth: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
    groups: PERMANENT_GROUPS,
    upper: true,
  },
  {
    key: "permanent-lower",
    type: "permanent",
    label: "Arcada permanente inferior",
    teeth: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
    groups: PERMANENT_GROUPS,
    upper: false,
  },
  {
    key: "deciduous-lower",
    type: "deciduous",
    label: "Dentição decídua inferior",
    teeth: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
    groups: DECIDUOUS_GROUPS,
    upper: false,
  },
];

const defaultTooth = (toothNumber) => ({
  tooth_number: toothNumber,
  status: "healthy",
  procedures: [],
  planned_procedures: [],
  notes: "",
  surfaces: {},
});

const normalizeTooth = (tooth) => ({
  ...defaultTooth(Number(tooth?.tooth_number)),
  ...tooth,
  tooth_number: Number(tooth?.tooth_number),
  surfaces:
    tooth?.surfaces && typeof tooth.surfaces === "object"
      ? tooth.surfaces
      : tooth?.faces && typeof tooth.faces === "object"
        ? tooth.faces
        : {},
  procedures: Array.isArray(tooth?.procedures) ? tooth.procedures : [],
  planned_procedures: Array.isArray(tooth?.planned_procedures)
    ? tooth.planned_procedures
    : [],
});

const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(`${String(birthDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() &&
      today.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const suggestedDentition = (patient) => {
  const age = calculateAge(patient?.birth_date);
  if (age === null) return "permanent";
  if (age < 6) return "deciduous";
  if (age <= 11) return "mixed";
  return "permanent";
};

const MiniFaceMap = ({ tooth }) => {
  const quadrant = Math.floor(Number(tooth.tooth_number) / 10);
  const upper = [1, 2, 5, 6].includes(quadrant);
  const rightSide = [1, 4, 5, 8].includes(quadrant);
  const surfaces = tooth.surfaces || {};
  const innerFace = upper ? "palatino" : "lingual";
  const topFace = upper ? "vestibular" : innerFace;
  const bottomFace = upper ? innerFace : "vestibular";
  const leftFace = rightSide ? "distal" : "mesial";
  const rightFace = rightSide ? "mesial" : "distal";
  const color = (face) => COLORS[surfaces[face]] || "#FFFFFF";
  const stroke = "#94A3B8";

  return (
    <svg viewBox="0 0 42 42" className="h-9 w-9" aria-hidden="true">
      <polygon
        points="2,2 40,2 30,12 12,12"
        fill={color(topFace)}
        stroke={stroke}
      />
      <polygon
        points="2,2 12,12 12,30 2,40"
        fill={color(leftFace)}
        stroke={stroke}
      />
      <rect
        x="12"
        y="12"
        width="18"
        height="18"
        fill={color("oclusal")}
        stroke={stroke}
      />
      <polygon
        points="40,2 40,40 30,30 30,12"
        fill={color(rightFace)}
        stroke={stroke}
      />
      <polygon
        points="2,40 12,30 30,30 40,40"
        fill={color(bottomFace)}
        stroke={stroke}
      />
    </svg>
  );
};

const GroupLabels = ({ groups, columns }) => (
  <div
    className="mb-1 grid text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {groups.map((group, index) => (
      <div
        key={`${group.label}-${index}`}
        className="border-b border-slate-300 pb-1"
        style={{ gridColumn: `span ${group.span} / span ${group.span}` }}
      >
        {group.label}
      </div>
    ))}
  </div>
);

const OdontogramFacesEmbed = ({ patientId, patient }) => {
  const currentPatientId = patientId || patient?.id;
  const currentPatientName = patient?.full_name || patient?.name || "Paciente";
  const [odontogram, setOdontogram] = useState(null);
  const [activeTooth, setActiveTooth] = useState(null);
  const [savingState, setSavingState] = useState("idle");
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAIReview, setShowAIReview] = useState(false);
  const lastRequest = useRef(0);

  const teethByNumber = useMemo(
    () =>
      new Map(
        (odontogram?.teeth || []).map((tooth) => {
          const normalized = normalizeTooth(tooth);
          return [normalized.tooth_number, normalized];
        }),
      ),
    [odontogram?.teeth],
  );

  useEffect(() => {
    if (!currentPatientId) return;
    let cancelled = false;

    const load = async () => {
      setOdontogram(null);
      try {
        const response = await fetch(
          `${backendUrl}/api/odontograma/patient/${currentPatientId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        if (!response.ok)
          throw new Error(`Buscar odontograma: ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setOdontogram({
            ...data,
            dentition_mode: data.dentition_mode || suggestedDentition(patient),
            teeth: (data.teeth || []).map(normalizeTooth),
          });
        }
      } catch (error) {
        console.error("[odontograma] erro ao buscar:", error);
        if (!cancelled) {
          setOdontogram({
            teeth: [],
            dentition_mode: suggestedDentition(patient),
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentPatientId, patient?.birth_date]);

  useEffect(() => {
    let active = true;
    setAiAnalysis(null);
    setShowAIReview(false);
    if (!currentPatientId) return undefined;

    listOdontogramReviews(currentPatientId)
      .then((items) => {
        if (active) setAiAnalysis(items[0] || null);
      })
      .catch(() => {
        // A indisponibilidade da IA não deve impedir o odontograma.
      });

    return () => {
      active = false;
    };
  }, [currentPatientId]);

  const saveOdontogram = async (nextTeeth, dentitionOverride = null) => {
    const requestId = ++lastRequest.current;
    const dentitionMode =
      dentitionOverride || odontogram?.dentition_mode || "permanent";
    setSavingState("saving");
    try {
      const response = await fetch(
        `${backendUrl}/api/odontograma/${currentPatientId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patient_name: currentPatientName,
            dentition_mode: dentitionMode,
            teeth: nextTeeth.map(({ faces, ...tooth }) => ({
              ...tooth,
              surfaces: tooth.surfaces || faces || {},
            })),
            general_notes: odontogram?.general_notes ?? null,
          }),
        },
      );
      if (!response.ok)
        throw new Error(`Salvar odontograma: ${response.status}`);
      const saved = await response.json();
      if (requestId === lastRequest.current) {
        setOdontogram({
          ...saved,
          dentition_mode: saved.dentition_mode || dentitionMode,
          teeth: (saved.teeth || []).map(normalizeTooth),
        });
        setSavingState("saved");
        if (!dentitionOverride) {
          setAiAnalysis((current) =>
            current ? { ...current, status: "stale" } : current,
          );
          setShowAIReview(true);
        }
      }
    } catch (error) {
      console.error("[odontograma] erro ao salvar:", error);
      if (requestId === lastRequest.current) setSavingState("error");
    }
  };

  const changeDentition = (dentitionMode) => {
    const nextTeeth = odontogram?.teeth || [];
    setOdontogram((previous) => ({
      ...(previous || {}),
      dentition_mode: dentitionMode,
    }));
    saveOdontogram(nextTeeth, dentitionMode);
  };

  const updateTooth = (toothNumber, updates) => {
    const currentTeeth = odontogram?.teeth || [];
    const exists = currentTeeth.some(
      (tooth) => Number(tooth.tooth_number) === Number(toothNumber),
    );
    const nextTooth = {
      ...(teethByNumber.get(Number(toothNumber)) ||
        defaultTooth(Number(toothNumber))),
      ...updates,
      tooth_number: Number(toothNumber),
      last_updated: new Date().toISOString(),
    };
    const nextTeeth = exists
      ? currentTeeth.map((tooth) =>
          Number(tooth.tooth_number) === Number(toothNumber)
            ? nextTooth
            : tooth,
        )
      : [...currentTeeth, nextTooth];

    setOdontogram((previous) => ({ ...(previous || {}), teeth: nextTeeth }));
    saveOdontogram(nextTeeth);
  };

  const renderTooth = (toothNumber, arch) => {
    const tooth = teethByNumber.get(toothNumber) || defaultTooth(toothNumber);
    const wholeColor =
      tooth.status && tooth.status !== "healthy" ? COLORS[tooth.status] : null;
    const hasSurfaces = Object.values(tooth.surfaces || {}).some(Boolean);
    const planned = (tooth.planned_procedures || []).length > 0;

    return (
      <button
        key={toothNumber}
        type="button"
        onClick={() => setActiveTooth(tooth)}
        className={`group flex min-w-[52px] flex-col items-center rounded-xl border px-1 py-2 transition hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-50 hover:shadow-sm ${
          wholeColor || hasSurfaces || planned
            ? "border-slate-300 bg-white"
            : "border-transparent bg-transparent"
        }`}
        title={`Dente ${toothNumber} — ${
          STATUS_LABELS[tooth.status] || "Saudável"
        }`}
      >
        {arch.upper && <MiniFaceMap tooth={tooth} />}
        <img
          src={`/odontograma/teeth/${toothNumber}.webp`}
          alt={`Anatomia do dente ${toothNumber}`}
          loading="lazy"
          draggable="false"
          className={`object-contain drop-shadow-sm ${
            arch.type === "deciduous" ? "h-20 w-16" : "h-24 w-[72px]"
          }`}
        />
        {!arch.upper && <MiniFaceMap tooth={tooth} />}
        <span
          className="mt-1 rounded px-1.5 py-0.5 text-xs font-bold"
          style={
            wholeColor
              ? { backgroundColor: wholeColor, color: "#FFFFFF" }
              : { color: "#334155" }
          }
        >
          {toothNumber}
        </span>
        {planned && (
          <span
            className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-400"
            title="Há procedimento planejado"
          />
        )}
      </button>
    );
  };

  if (!odontogram) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-xl border bg-white">
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
        <span className="ml-3 text-sm text-slate-600">
          Carregando odontograma...
        </span>
      </div>
    );
  }

  const dentitionMode = odontogram.dentition_mode || "permanent";
  const visibleArches = ARCHES.filter(
    (arch) => dentitionMode === "mixed" || arch.type === dentitionMode,
  );

  return (
    <div className="rounded-xl border bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div>
          <h3 className="font-semibold text-slate-900">Odontograma</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Clique em qualquer dente para registrar a condição geral ou marcar
            suas faces.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <button
            type="button"
            onClick={() => setShowAIReview(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100"
          >
            <Sparkles className="h-4 w-4" />
            {aiAnalysis?.status === "stale"
              ? "Revisar novamente"
              : aiAnalysis?.status === "accepted"
                ? "IA revisada"
                : "Revisar com IA"}
          </button>
          <div>
          <div className="mb-1 text-xs font-medium text-slate-600">
            Dentição exibida
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            {DENTITION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => changeDentition(option.value)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  dentitionMode === option.value
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-slate-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          </div>
        </div>
      </div>

      {showAIReview && (
        <OdontogramAIReviewCard
          patientId={currentPatientId}
          existingAnalysis={aiAnalysis}
          onAnalysisChange={setAiAnalysis}
          onClose={() => setShowAIReview(false)}
        />
      )}

      <div className="overflow-x-auto bg-slate-50 px-4 py-5">
        <div className="mx-auto min-w-[940px] max-w-[1400px] space-y-5">
          {visibleArches.map((arch, index) => {
            const beginsLowerArch = !arch.upper;
            return (
              <section
                key={arch.key}
                className={
                  beginsLowerArch && index > 0
                    ? "border-t border-slate-300 pt-5"
                    : ""
                }
                aria-label={arch.label}
              >
                <div
                  className="mx-auto"
                  style={{
                    width:
                      arch.type === "deciduous"
                        ? "min(100%, 720px)"
                        : "min(100%, 1180px)",
                  }}
                >
                  {arch.upper && (
                    <GroupLabels
                      groups={arch.groups}
                      columns={arch.teeth.length}
                    />
                  )}
                  <div className="flex justify-center gap-1">
                    {arch.teeth.map((toothNumber) =>
                      renderTooth(toothNumber, arch),
                    )}
                  </div>
                  {!arch.upper && (
                    <div className="mt-1">
                      <GroupLabels
                        groups={arch.groups}
                        columns={arch.teeth.length}
                      />
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <div className="border-t px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600">
            {[
              ["carious", "Cárie"],
              ["restored", "Restaurado"],
              ["extracted", "Extraído"],
              ["crown", "Coroa"],
              ["fractured", "Fratura"],
              ["prosthesis", "Prótese"],
            ].map(([key, label]) => (
              <span key={key} className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded"
                  style={{ backgroundColor: COLORS[key] }}
                />
                {label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-teal-400" />
              Planejado
            </span>
          </div>

          <div className="flex items-center text-xs">
            {savingState === "saving" && (
              <span className="flex items-center text-slate-500">
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Salvando...
              </span>
            )}
            {(savingState === "idle" || savingState === "saved") && (
              <span className="flex items-center text-emerald-700">
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                Alterações salvas
              </span>
            )}
            {savingState === "error" && (
              <span className="flex items-center text-red-700">
                <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
                Não foi possível salvar. Tente novamente.
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 text-[10px] text-slate-400">
          Modelos anatômicos: University of Dundee, School of Dentistry · CC BY
          4.0 · modelos adaptados e renderizados para o OdontoAssist AI.
        </div>
      </div>

      {activeTooth && (
        <ToothModal
          isOpen
          tooth={activeTooth}
          getToothStatusLabel={(status) => STATUS_LABELS[status]}
          onClose={() => setActiveTooth(null)}
          onUpdate={updateTooth}
        />
      )}
    </div>
  );
};

export default OdontogramFacesEmbed;
